import React, { useEffect, useRef, useState, useMemo } from "react";
import {
    createChart, CandlestickSeries, LineSeries, AreaSeries, HistogramSeries, LineStyle,
} from "lightweight-charts";
import { Activity, Layers, Ruler, BarChart2, GitBranch, Trash2 } from "lucide-react";

/**
 * Interactive financial chart powered by TradingView lightweight-charts.
 * Supports candlestick / line / area, with native pinch-zoom and pan.
 * Optional indicators: BB, MA50, MA200, Fibonacci, Volume, RSI panel, Price Range tool.
 */

const DEFAULT_INDICATORS = {
    ma20: true,      // momento corrente 20-period MA (toggleable)
    bb: false,
    ma50: false,
    ma200: false,
    fib: false,
    volume: true,
    rsi: false,
    priceRange: false,
};

const FIB_COLORS = {
    level_0: "#888",
    level_236: "#ff8c42",
    level_382: "#fbbf24",
    level_500: "#d4af37",
    level_618: "#84cc16",
    level_786: "#22d3ee",
    level_100: "#888",
};

export const TradingChart = ({
    candles = [],
    chartType = "candle",
    height = 360,
    fibonacci = null,
    showIndicatorsPanel = true,
    defaultIndicators = {},
    analystLevels = null,
}) => {
    const containerRef = useRef(null);
    const rsiRef = useRef(null);
    const chartRef = useRef(null);
    const rsiChartRef = useRef(null);
    const priceSeriesRef = useRef(null);
    // TradingView-style drag-to-measure overlay state
    const dragRef = useRef({ active: false, startX: 0, startY: 0, startPrice: 0, startTime: null });

    const [indicators, setIndicators] = useState({ ...DEFAULT_INDICATORS, ...defaultIndicators });
    const [rangeInfo, setRangeInfo] = useState(null); // {start, end, pct, abs}
    const [dragBox, setDragBox] = useState(null);     // {x1,y1,x2,y2,startPrice,endPrice,abs,pct,bars}

    const hasRSIData = useMemo(() => candles.some(c => c.rsi != null), [candles]);
    const hasMA200Data = useMemo(() => candles.some(c => c.ma200 != null), [candles]);

    const toggle = (key) => setIndicators(s => ({ ...s, [key]: !s[key] }));

    useEffect(() => {
        const el = containerRef.current;
        if (!el || !candles.length) return undefined;

        const chart = createChart(el, {
            width: el.clientWidth,
            height,
            layout: {
                background: { color: "#0a0a0b" },
                textColor: "#8a8a92",
                fontFamily: "JetBrains Mono, monospace",
            },
            grid: {
                vertLines: { color: "rgba(59,130,246,0.05)" },
                horzLines: { color: "rgba(59,130,246,0.05)" },
            },
            rightPriceScale: { borderColor: "#1a2230" },
            timeScale: { borderColor: "#1a2230", timeVisible: true, secondsVisible: false },
            crosshair: {
                vertLine: { color: "#3b82f6", style: 0, width: 1 },
                horzLine: { color: "#3b82f6", style: 0, width: 1 },
            },
            handleScroll: {
                mouseWheel: !indicators.priceRange,
                pressedMouseMove: !indicators.priceRange,
                horzTouchDrag: true,
                vertTouchDrag: false,
            },
            handleScale: {
                mouseWheel: !indicators.priceRange,
                pinch: true,
                axisPressedMouseMove: true,
            },
        });
        chartRef.current = chart;

        const data = candles.map(c => ({ ...c, time: c.date }));

        // Main price series
        let priceSeries;
        if (chartType === "candle") {
            priceSeries = chart.addSeries(CandlestickSeries, {
                upColor: "#00ff95",
                downColor: "#ff3b5c",
                borderUpColor: "#00ff95",
                borderDownColor: "#ff3b5c",
                wickUpColor: "#00ff95",
                wickDownColor: "#ff3b5c",
            });
            priceSeries.setData(data.map(d => ({ time: d.time, open: d.open, high: d.high, low: d.low, close: d.close })));
        } else if (chartType === "area") {
            priceSeries = chart.addSeries(AreaSeries, {
                lineColor: "#3b82f6",
                topColor: "rgba(59,130,246,0.45)",
                bottomColor: "rgba(34,197,94,0.05)",
                lineWidth: 2,
            });
            priceSeries.setData(data.map(d => ({ time: d.time, value: d.close })));
        } else {
            priceSeries = chart.addSeries(LineSeries, { color: "#3b82f6", lineWidth: 2 });
            priceSeries.setData(data.map(d => ({ time: d.time, value: d.close })));
        }
        priceSeriesRef.current = priceSeries;

        // MA20 baseline overlay — toggleable (only on candle/line chart types)
        if (indicators.ma20 && chartType !== "area") {
            const ma = chart.addSeries(LineSeries, {
                color: "#22c55e",
                lineWidth: 1,
                priceLineVisible: false,
                lastValueVisible: false,
            });
            ma.setData(data.filter(d => d.ma20 != null).map(d => ({ time: d.time, value: d.ma20 })));
        }

        // MA50
        if (indicators.ma50) {
            const s = chart.addSeries(LineSeries, {
                color: "#22d3ee", lineWidth: 1, priceLineVisible: false, lastValueVisible: false,
            });
            s.setData(data.filter(d => d.ma50 != null).map(d => ({ time: d.time, value: d.ma50 })));
        }
        // MA200
        if (indicators.ma200) {
            const s = chart.addSeries(LineSeries, {
                color: "#ff5fd2", lineWidth: 1.5, priceLineVisible: false, lastValueVisible: false,
            });
            s.setData(data.filter(d => d.ma200 != null).map(d => ({ time: d.time, value: d.ma200 })));
        }
        // Bollinger Bands
        if (indicators.bb) {
            const up = chart.addSeries(LineSeries, {
                color: "rgba(59,130,246,0.7)", lineWidth: 1, lineStyle: LineStyle.Dashed,
                priceLineVisible: false, lastValueVisible: false,
            });
            up.setData(data.filter(d => d.bb_upper != null).map(d => ({ time: d.time, value: d.bb_upper })));
            const lo = chart.addSeries(LineSeries, {
                color: "rgba(59,130,246,0.7)", lineWidth: 1, lineStyle: LineStyle.Dashed,
                priceLineVisible: false, lastValueVisible: false,
            });
            lo.setData(data.filter(d => d.bb_lower != null).map(d => ({ time: d.time, value: d.bb_lower })));
        }
        // Fibonacci levels as horizontal price lines on price series
        if (indicators.fib && fibonacci) {
            ["level_0", "level_236", "level_382", "level_500", "level_618", "level_786", "level_100"]
                .forEach(k => {
                    if (fibonacci[k] != null) {
                        priceSeries.createPriceLine({
                            price: fibonacci[k],
                            color: FIB_COLORS[k],
                            lineWidth: 1,
                            lineStyle: LineStyle.Dotted,
                            axisLabelVisible: true,
                            title: k.replace("level_", "Fib ").replace("0", "0%").replace("236", "23.6%").replace("382", "38.2%").replace("500", "50%").replace("618", "61.8%").replace("786", "78.6%").replace("Fib 100", "Fib 100%"),
                        });
                    }
                });
        }

        // Volume histogram
        if (indicators.volume) {
            const vol = chart.addSeries(HistogramSeries, {
                priceFormat: { type: "volume" },
                priceScaleId: "vol",
            });
            vol.priceScale().applyOptions({ scaleMargins: { top: 0.85, bottom: 0 } });
            vol.setData(data.map(d => ({
                time: d.time,
                value: d.volume || 0,
                color: d.close >= d.open ? "rgba(0,255,149,0.35)" : "rgba(255,59,92,0.35)",
            })));
        }

        // ---------- RSI sub-chart ----------
        let rsiChart;
        if (indicators.rsi && hasRSIData && rsiRef.current) {
            rsiChart = createChart(rsiRef.current, {
                width: rsiRef.current.clientWidth,
                height: 110,
                layout: { background: { color: "#0a0a0b" }, textColor: "#8a8a92", fontFamily: "JetBrains Mono, monospace" },
                grid: { vertLines: { color: "rgba(59,130,246,0.05)" }, horzLines: { color: "rgba(59,130,246,0.05)" } },
                rightPriceScale: { borderColor: "#1a2230" },
                timeScale: { borderColor: "#1a2230", visible: false },
                crosshair: { vertLine: { color: "#3b82f6" }, horzLine: { color: "#3b82f6" } },
            });
            rsiChartRef.current = rsiChart;
            const rsiSeries = rsiChart.addSeries(LineSeries, { color: "#3b82f6", lineWidth: 1.5, priceLineVisible: false });
            rsiSeries.setData(data.filter(d => d.rsi != null).map(d => ({ time: d.time, value: d.rsi })));
            // Overbought/Oversold lines
            rsiSeries.createPriceLine({ price: 70, color: "#ef4444", lineWidth: 1, lineStyle: LineStyle.Dashed, axisLabelVisible: true, title: "70" });
            rsiSeries.createPriceLine({ price: 30, color: "#22c55e", lineWidth: 1, lineStyle: LineStyle.Dashed, axisLabelVisible: true, title: "30" });
            rsiSeries.createPriceLine({ price: 50, color: "#555", lineWidth: 1, lineStyle: LineStyle.Dotted, axisLabelVisible: false });

            // Sync time scale between main chart and RSI chart
            const sync = (range) => { if (range) rsiChart.timeScale().setVisibleLogicalRange(range); };
            chart.timeScale().subscribeVisibleLogicalRangeChange(sync);
        }

        chart.timeScale().fitContent();

        // ---------- Price Range Tool (TradingView-style drag-to-measure) ----------
        // Hold left button, drag from A to B, release → rectangle overlay with
        // delta + percentage stays visible until you click again or toggle off.
        // Mousedown is captured by the transparent overlay <div> rendered when
        // `indicators.priceRange` is true (lightweight-charts owns the canvas
        // pointer events otherwise). We listen on `window` so drag continues
        // even if the cursor leaves the chart bounds.
        const onMouseDown = (e) => {
            if (!indicators.priceRange) return;
            // Only fire when the press starts inside the chart container
            const rect = el.getBoundingClientRect();
            if (e.clientX < rect.left || e.clientX > rect.right || e.clientY < rect.top || e.clientY > rect.bottom) return;
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;
            const startPrice = priceSeries.coordinateToPrice(y);
            if (startPrice == null) return;
            const startTime = chart.timeScale().coordinateToTime(x);
            dragRef.current = { active: true, startX: x, startY: y, startPrice, startTime };
            setDragBox({ x1: x, y1: y, x2: x, y2: y, startPrice, endPrice: startPrice, abs: 0, pct: 0, bars: 0 });
            e.preventDefault();
        };

        const onMouseMove = (e) => {
            if (!dragRef.current.active) return;
            const rect = el.getBoundingClientRect();
            const x = Math.max(0, Math.min(rect.width, e.clientX - rect.left));
            const y = Math.max(0, Math.min(rect.height, e.clientY - rect.top));
            const endPrice = priceSeries.coordinateToPrice(y);
            if (endPrice == null) return;
            const startTime = dragRef.current.startTime;
            const endTime = chart.timeScale().coordinateToTime(x);
            // Bar count between A and B (best-effort using indices of nearest candles)
            let bars = 0;
            if (startTime && endTime && Array.isArray(data) && data.length) {
                const iA = data.findIndex(d => d.time === startTime);
                const iB = data.findIndex(d => d.time === endTime);
                if (iA >= 0 && iB >= 0) bars = Math.abs(iB - iA);
            }
            const startPrice = dragRef.current.startPrice;
            const abs = endPrice - startPrice;
            const pct = startPrice ? (abs / startPrice) * 100 : 0;
            setDragBox({
                x1: dragRef.current.startX, y1: dragRef.current.startY,
                x2: x, y2: y,
                startPrice, endPrice, abs, pct, bars,
            });
        };

        const onMouseUp = () => {
            if (!dragRef.current.active) return;
            dragRef.current.active = false;
            setDragBox(prev => {
                if (!prev) return null;
                setRangeInfo({ start: prev.startPrice, end: prev.endPrice, abs: prev.abs, pct: prev.pct, bars: prev.bars });
                return prev;
            });
        };

        el.addEventListener("mousedown", onMouseDown);
        window.addEventListener("mousedown", onMouseDown);
        window.addEventListener("mousemove", onMouseMove);
        window.addEventListener("mouseup", onMouseUp);

        // Responsive resize
        const ro = new ResizeObserver(() => {
            if (el && chart) chart.applyOptions({ width: el.clientWidth });
            if (rsiRef.current && rsiChart) rsiChart.applyOptions({ width: rsiRef.current.clientWidth });
        });
        ro.observe(el);

        return () => {
            ro.disconnect();
            el.removeEventListener("mousedown", onMouseDown);
            window.removeEventListener("mousedown", onMouseDown);
            window.removeEventListener("mousemove", onMouseMove);
            window.removeEventListener("mouseup", onMouseUp);
            chart.remove();
            if (rsiChart) rsiChart.remove();
            dragRef.current = { active: false, startX: 0, startY: 0, startPrice: 0, startTime: null };
        };
    }, [candles, chartType, height, indicators, fibonacci, hasRSIData]);

    // Draw analyst S/R levels as persistent priceLine overlays.
    // We attach them in a separate effect so toggling the AI panel doesn't
    // recreate the whole chart (which loses zoom state).
    useEffect(() => {
        const series = priceSeriesRef.current;
        if (!series || !analystLevels) return undefined;
        const lines = [];
        const draw = (price, color, title) => {
            try {
                const ln = series.createPriceLine({
                    price, color, lineWidth: 1, lineStyle: LineStyle.Dashed,
                    axisLabelVisible: true, title,
                });
                lines.push(ln);
            } catch (_) { /* ignored — chart disposed */ }
        };
        (analystLevels.supports || []).forEach((lv, i) => draw(lv.price, "#22c55e", `S${i + 1} ${lv.price}`));
        (analystLevels.resistances || []).forEach((lv, i) => draw(lv.price, "#ef4444", `R${i + 1} ${lv.price}`));
        return () => {
            lines.forEach(ln => { try { series.removePriceLine(ln); } catch (_) { /* noop */ } });
        };
    }, [analystLevels]);

    const clearRange = () => {
        setRangeInfo(null);
        setDragBox(null);
        dragRef.current = { active: false, startX: 0, startY: 0, startPrice: 0, startTime: null };
    };

    return (
        <div className="space-y-2">
            {showIndicatorsPanel && (
                <div className="flex items-center gap-1.5 flex-wrap text-[10px] uppercase tracking-wider">
                    <span className="text-mute mr-1">Indicatori:</span>
                    <IndicatorChip active={indicators.ma20} onClick={() => toggle("ma20")} icon={GitBranch} label="MA20" testid="ind-ma20" />
                    <IndicatorChip active={indicators.bb} onClick={() => toggle("bb")} icon={Layers} label="BB" testid="ind-bb" />
                    <IndicatorChip active={indicators.ma50} onClick={() => toggle("ma50")} icon={GitBranch} label="MA50" testid="ind-ma50" />
                    <IndicatorChip active={indicators.ma200} onClick={() => toggle("ma200")} icon={GitBranch} label="MA200" testid="ind-ma200" disabled={!hasMA200Data} />
                    <IndicatorChip active={indicators.fib} onClick={() => toggle("fib")} icon={Layers} label="FIB" testid="ind-fib" disabled={!fibonacci} />
                    <IndicatorChip active={indicators.volume} onClick={() => toggle("volume")} icon={BarChart2} label="VOL" testid="ind-volume" />
                    <IndicatorChip active={indicators.rsi} onClick={() => toggle("rsi")} icon={Activity} label="RSI" testid="ind-rsi" disabled={!hasRSIData} />
                    <IndicatorChip active={indicators.priceRange} onClick={() => toggle("priceRange")} icon={Ruler} label="MISURA %" testid="ind-range" />
                    {rangeInfo && rangeInfo.end != null && (
                        <div data-testid="range-result" className="ml-2 px-2 py-1 border border-amber flex items-center gap-2" style={{ color: "var(--amber)" }}>
                            <span>Δ {rangeInfo.abs.toFixed(2)}</span>
                            <span style={{ color: rangeInfo.pct >= 0 ? "var(--green)" : "var(--red)" }}>
                                {rangeInfo.pct >= 0 ? "+" : ""}{rangeInfo.pct.toFixed(2)}%
                            </span>
                            {rangeInfo.bars > 0 && <span className="text-mute">· {rangeInfo.bars} bar</span>}
                            <button onClick={clearRange} className="text-mute hover:text-red-down ml-1"><Trash2 size={12} /></button>
                        </div>
                    )}
                </div>
            )}
            {indicators.priceRange && !rangeInfo?.end && (
                <div data-testid="range-hint" className="text-[10px] text-mute">
                    Misura attiva — clicca, trascina e rilascia su due punti del grafico (stile TradingView)
                </div>
            )}
            <div ref={containerRef} data-testid="trading-chart" style={{ width: "100%", height, touchAction: "pan-y", position: "relative", cursor: indicators.priceRange ? "crosshair" : "default" }}>
                {indicators.priceRange && (
                    <div
                        data-testid="measure-capture-layer"
                        style={{ position: "absolute", inset: 0, zIndex: 4, cursor: "crosshair", background: "transparent" }}
                    />
                )}
                {dragBox && indicators.priceRange && (
                    <DragMeasureOverlay box={dragBox} />
                )}
            </div>
            {indicators.rsi && hasRSIData && (
                <div data-testid="rsi-pane" className="border-t border-soft pt-1">
                    <div className="text-[9px] text-mute tracking-widest px-1">RSI (14)</div>
                    <div ref={rsiRef} style={{ width: "100%", height: 110, touchAction: "pan-y" }} />
                </div>
            )}
        </div>
    );
};

// Drag-to-measure overlay (TradingView-style). Draws a translucent rectangle
// from A (mousedown) to B (current cursor / mouseup) with a center label
// showing absolute delta, percentage, and bar count between the two points.
function DragMeasureOverlay({ box }) {
    const { x1, y1, x2, y2, abs, pct, bars, startPrice, endPrice } = box;
    const left = Math.min(x1, x2);
    const top = Math.min(y1, y2);
    const width = Math.abs(x2 - x1);
    const height = Math.abs(y2 - y1);
    const goingUp = pct >= 0;
    const fillColor = goingUp ? "rgba(34,197,94,0.10)" : "rgba(239,68,68,0.10)";
    const strokeColor = goingUp ? "#22c55e" : "#ef4444";
    const labelText = `${goingUp ? "+" : ""}${pct.toFixed(2)}%  ·  ${goingUp ? "+" : ""}${abs.toFixed(2)}${bars ? `  ·  ${bars} bar${bars === 1 ? "" : "s"}` : ""}`;
    return (
        <div
            data-testid="measure-overlay"
            style={{
                position: "absolute",
                left, top, width, height,
                background: fillColor,
                border: `1px solid ${strokeColor}`,
                pointerEvents: "none",
                zIndex: 5,
            }}
        >
            {/* Top A price label */}
            <div style={{
                position: "absolute", top: y1 > y2 ? "auto" : -18, bottom: y1 > y2 ? -18 : "auto", left: x1 > x2 ? "auto" : 0, right: x1 > x2 ? 0 : "auto",
                fontFamily: "JetBrains Mono, monospace", fontSize: 10, color: "#9ca3af", whiteSpace: "nowrap",
            }}>A {startPrice?.toFixed?.(2)}</div>
            {/* Center label */}
            {width > 60 && height > 24 && (
                <div style={{
                    position: "absolute", left: "50%", top: "50%", transform: "translate(-50%, -50%)",
                    background: strokeColor, color: "#0a0a0b", padding: "3px 8px",
                    fontFamily: "JetBrains Mono, monospace", fontSize: 11, fontWeight: 700, whiteSpace: "nowrap",
                }}>{labelText}</div>
            )}
            {/* Bottom B price label */}
            <div style={{
                position: "absolute", top: y1 > y2 ? -18 : "auto", bottom: y1 > y2 ? "auto" : -18, left: x1 > x2 ? 0 : "auto", right: x1 > x2 ? "auto" : 0,
                fontFamily: "JetBrains Mono, monospace", fontSize: 10, color: strokeColor, whiteSpace: "nowrap",
            }}>B {endPrice?.toFixed?.(2)}</div>
        </div>
    );
}

const IndicatorChip = ({ active, onClick, icon: Icon, label, testid, disabled }) => (
    <button
        data-testid={testid}
        onClick={onClick}
        disabled={disabled}
        className="flex items-center gap-1 px-2 py-1 border text-[10px] font-semibold disabled:opacity-30 disabled:cursor-not-allowed"
        style={
            active
                ? { background: "rgba(212,175,55,0.15)", borderColor: "var(--amber)", color: "var(--amber)" }
                : { borderColor: "var(--border-soft, #2a2a30)", color: "var(--text-dim, #6a6a72)" }
        }
    >
        <Icon size={10} />
        <span>{label}</span>
    </button>
);
